/* @flow */

/*
 * manages whether operations that are waiting for the server are currently going on.
 * this state is useful to show user that certain data is currently being fetched
 * or operations are waiting to be finished.
 */

import { includes } from 'lodash'

import type { Action, RootState, PollId, Pending } from '../types'

import { ActionRequiresPropertyError, ensurePresenceInArray } from '../helpers'

export const STATE_KEY: string = 'pending'

export const SET_IS_FETCHING_POLLS: string = 'opinary/SET_IS_FETCHING_POLLS'
export const SET_IS_FETCHING_POLL_WHERE_ID: string =
  'opinary/SET_IS_FETCHING_POLL_WHERE_ID'
export const SET_IS_FETCHING_ICONS: string = 'opinary/SET_IS_FETCHING_ICONS'
export const SET_IS_FETCHING_USERS: string = 'opinary/SET_IS_FETCHING_USERS'
export const SET_IS_FETCHING_CURRENT_USER_INITIALLY: string =
  'opinary/SET_IS_FETCHING_CURRENT_USER_INITIALLY'
export const SET_IS_LOGGING_IN: string = 'opinary/SET_IS_LOGGING_IN'
export const SET_IS_SAVING_POLL_WHERE_ID: string =
  'opinary/SET_IS_SAVING_POLL_WHERE_ID'
export const SET_IS_CREATING_POLL: string = 'opinary/SET_IS_CREATING_POLL'
export const SET_IS_DELETING_POLL_WHERE_ID: string =
  'opinary/SET_IS_DELETING_POLL_WHERE_ID'

export function setIsFetchingPolls(isPending: boolean): Action {
  return {
    type: SET_IS_FETCHING_POLLS,
    isPending,
  }
}

export function setIsFetchingPollWhereId(
  id: PollId,
  isPending: boolean
): Action {
  return {
    type: SET_IS_FETCHING_POLL_WHERE_ID,
    id,
    isPending,
  }
}

export function setIsFetchingIcons(isPending: boolean): Action {
  return {
    type: SET_IS_FETCHING_ICONS,
    isPending,
  }
}

export function setIsFetchingUsers(isPending: boolean): Action {
  return {
    type: SET_IS_FETCHING_USERS,
    isPending,
  }
}

export function setIsFetchingCurrentUserInitially(isPending: boolean): Action {
  return {
    type: SET_IS_FETCHING_CURRENT_USER_INITIALLY,
    isPending,
  }
}

export function setIsLoggingIn(isPending: boolean): Action {
  return {
    type: SET_IS_LOGGING_IN,
    isPending,
  }
}

export function setIsSavingPollWhereId(id: PollId, isPending: boolean): Action {
  return {
    type: SET_IS_SAVING_POLL_WHERE_ID,
    id,
    isPending,
  }
}

export function setIsCreatingPoll(isPending: boolean): Action {
  return {
    type: SET_IS_CREATING_POLL,
    isPending,
  }
}

export function setIsDeletingPollWhereId(
  id: PollId,
  isPending: boolean
): Action {
  return {
    type: SET_IS_DELETING_POLL_WHERE_ID,
    id,
    isPending,
  }
}

export function selectIsFetchingPolls(rootState: RootState): boolean {
  return rootState[STATE_KEY].isFetchingPolls
}

export function selectIsFetchingPollWhereId(
  rootState: RootState,
  id: PollId
): boolean {
  return includes(rootState[STATE_KEY].isFetchingPollWhereId, id)
}

export function selectIsFetchingIcons(rootState: RootState): boolean {
  return rootState[STATE_KEY].isFetchingIcons
}

export function selectIsFetchingUsers(rootState: RootState): boolean {
  return rootState[STATE_KEY].isFetchingUsers
}

export function selectIsFetchingCurrentUserInitially(
  rootState: RootState
): boolean {
  return rootState[STATE_KEY].isFetchingCurrentUserInitially
}

export function selectIsLoggingIn(rootState: RootState): boolean {
  return rootState[STATE_KEY].isLoggingIn
}

export function selectIsSavingPollWhereId(
  rootState: RootState,
  id: PollId
): boolean {
  return includes(rootState[STATE_KEY].isSavingPollWhereId, id)
}

export function selectIsCreatingPoll(rootState: RootState): boolean {
  return rootState[STATE_KEY].isCreatingPoll
}

export function selectIsDeletingPollWhereId(
  rootState: RootState,
  id: PollId
): boolean {
  return includes(rootState[STATE_KEY].isDeletingPollWhereId, id)
}

const initialState: Pending = {
  isFetchingPolls: false,
  isFetchingPollWhereId: [],
  isFetchingIcons: false,
  isFetchingUsers: false,
  isFetchingCurrentUserInitially: false,
  isLoggingIn: false,
  isSavingPollWhereId: [],
  isCreatingPoll: false,
  isDeletingPollWhereId: [],
}

export default function reducer(
  state: Pending = initialState,
  action: Action
): ?Pending {
  switch (action.type) {
    case SET_IS_FETCHING_POLLS:
      if (action.isPending == null) {
        throw new ActionRequiresPropertyError(
          SET_IS_FETCHING_POLLS,
          'isPending'
        )
      }
      return Object.assign({}, state, { isFetchingPolls: action.isPending })
    case SET_IS_FETCHING_POLL_WHERE_ID:
      if (action.isPending == null) {
        throw new ActionRequiresPropertyError(
          SET_IS_FETCHING_POLL_WHERE_ID,
          'isPending'
        )
      }
      if (action.id == null) {
        throw new ActionRequiresPropertyError(
          SET_IS_FETCHING_POLL_WHERE_ID,
          'id'
        )
      }
      return Object.assign({}, state, {
        isFetchingPollWhereId: ensurePresenceInArray(
          state.isFetchingPollWhereId,
          action.id,
          action.isPending
        ),
      })
    case SET_IS_FETCHING_ICONS:
      if (action.isPending == null) {
        throw new ActionRequiresPropertyError(
          SET_IS_FETCHING_ICONS,
          'isPending'
        )
      }
      return Object.assign({}, state, { isFetchingIcons: action.isPending })
    case SET_IS_FETCHING_USERS:
      if (action.isPending == null) {
        throw new ActionRequiresPropertyError(
          SET_IS_FETCHING_USERS,
          'isPending'
        )
      }
      return Object.assign({}, state, { isFetchingUsers: action.isPending })
    case SET_IS_FETCHING_CURRENT_USER_INITIALLY:
      if (action.isPending == null) {
        throw new ActionRequiresPropertyError(
          SET_IS_FETCHING_CURRENT_USER_INITIALLY,
          'isPending'
        )
      }
      return Object.assign({}, state, {
        isFetchingCurrentUserInitially: action.isPending,
      })
    case SET_IS_LOGGING_IN:
      if (action.isPending == null) {
        throw new ActionRequiresPropertyError(SET_IS_LOGGING_IN, 'isPending')
      }
      return Object.assign({}, state, { isLoggingIn: action.isPending })
    case SET_IS_SAVING_POLL_WHERE_ID:
      if (action.isPending == null) {
        throw new ActionRequiresPropertyError(
          SET_IS_SAVING_POLL_WHERE_ID,
          'isPending'
        )
      }
      if (action.id == null) {
        throw new ActionRequiresPropertyError(SET_IS_SAVING_POLL_WHERE_ID, 'id')
      }
      return Object.assign({}, state, {
        isSavingPollWhereId: ensurePresenceInArray(
          state.isSavingPollWhereId,
          action.id,
          action.isPending
        ),
      })
    case SET_IS_CREATING_POLL:
      if (action.isPending == null) {
        throw new ActionRequiresPropertyError(SET_IS_CREATING_POLL, 'isPending')
      }
      return Object.assign({}, state, { isCreatingPoll: action.isPending })
    case SET_IS_DELETING_POLL_WHERE_ID:
      if (action.isPending == null) {
        throw new ActionRequiresPropertyError(
          SET_IS_DELETING_POLL_WHERE_ID,
          'isPending'
        )
      }
      if (action.id == null) {
        throw new ActionRequiresPropertyError(
          SET_IS_DELETING_POLL_WHERE_ID,
          'id'
        )
      }
      return Object.assign({}, state, {
        isDeletingPollWhereId: ensurePresenceInArray(
          state.isDeletingPollWhereId,
          action.id,
          action.isPending
        ),
      })
    default:
      return state
  }
}



// WEBPACK FOOTER //
// ./src/stores/pending.js